import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { Plus, Edit, Trash2, Check, Minus } from 'lucide-react';
import DataTable from '../DataTable';
import DiseaseModal from './DiseaseModal';
import ConfirmDialog from '../ConfirmDialog';
import IconButton from '../common/IconButton';
import SmartSearchBar from '../leads/SmartSearchBar';
import { DiseaseDto, CreateDiseaseDto, UpdateDiseaseDto, diseasesApi } from '../../services/settingsService';

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;

  @media (max-width: 767px) {
    max-width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CenteredCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TextCell = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const SecondaryText = styled.span`
  font-size: 13px;
  color: var(--text-secondary);
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
`;

const DiseasesTab: React.FC = () => {
  const [diseases, setDiseases] = useState<DiseaseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDisease, setSelectedDisease] = useState<DiseaseDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [diseaseToDelete, setDiseaseToDelete] = useState<DiseaseDto | null>(null);

  useEffect(() => {
    loadDiseases();
  }, []);

  const loadDiseases = async () => {
    try {
      setLoading(true);
      const data = await diseasesApi.getAll();
      setDiseases(data);
    } catch (error) {
      console.error('שגיאה בטעינת מחלות:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter diseases based on search
  const filteredDiseases = useMemo(() => {
    if (!searchTerm) return diseases;
    const search = searchTerm.toLowerCase();
    return diseases.filter(disease =>
      disease.name.toLowerCase().includes(search) ||
      disease.categoryName?.toLowerCase().includes(search) ||
      disease.typeName?.toLowerCase().includes(search)
    );
  }, [diseases, searchTerm]);

  const handleAddDisease = () => {
    setSelectedDisease(null);
    setModalOpen(true);
  };

  const handleEditDisease = (disease: DiseaseDto) => {
    setSelectedDisease(disease);
    setModalOpen(true);
  };

  const handleDeleteDisease = (disease: DiseaseDto) => {
    setDiseaseToDelete(disease);
    setDeleteConfirmOpen(true);
  };

  const handleSaveDisease = async (diseaseData: CreateDiseaseDto | UpdateDiseaseDto) => {
    try {
      if (selectedDisease) {
        await diseasesApi.update(selectedDisease.id, diseaseData as UpdateDiseaseDto);
      } else {
        await diseasesApi.create(diseaseData as CreateDiseaseDto);
      }
      await loadDiseases();
    } catch (error) {
      console.error('שגיאה בשמירת מחלה:', error);
      throw error;
    }
  };

  const confirmDelete = async () => {
    if (!diseaseToDelete) return;

    try {
      await diseasesApi.delete(diseaseToDelete.id);
      await loadDiseases();
      setDeleteConfirmOpen(false);
      setDiseaseToDelete(null);
    } catch (error) {
      console.error('שגיאה במחיקת מחלה:', error);
    }
  };

  const columns = [
    {
      key: 'name',
      title: 'שם המחלה',
      width: '200px',
      render: (value: string) => (
        <CenteredCell>
          <TextCell>{value}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'categoryName',
      title: 'קטגוריה',
      width: '150px',
      render: (value: string) => (
        <CenteredCell>
          <TextCell>{value || '-'}</TextCell>
        </CenteredCell>
      )
    },
    {
      key: 'typeName',
      title: 'סוג',
      width: '120px',
      render: (value: string) => (
        <CenteredCell>
          <SecondaryText>{value || '-'}</SecondaryText>
        </CenteredCell>
      )
    },
    {
      key: 'customFields',
      title: 'שדות נוספים',
      width: '120px',
      render: (fields: any[]) => (
        <CenteredCell>
          <SecondaryText>{fields?.length || 0} שדות</SecondaryText>
        </CenteredCell>
      )
    },
    {
      key: 'isActive',
      title: 'סטטוס',
      width: '80px',
      render: (value: boolean) => (
        <CenteredCell>
          {value ? (
            <Check size={14} color="#16a34a" strokeWidth={2.5} />
          ) : (
            <Minus size={14} color="#d1d5db" />
          )}
        </CenteredCell>
      )
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '100px',
      render: (_: any, disease: DiseaseDto) => (
        <ActionsCell>
          <IconButton
            icon={Edit}
            onClick={() => handleEditDisease(disease)}
            title="עריכה"
          />
          <IconButton
            icon={Trash2}
            onClick={() => handleDeleteDisease(disease)}
            title="מחיקה"
            variant="danger"
          />
        </ActionsCell>
      )
    }
  ];

  return (
    <>
      <TabHeader>
        <SearchWrapper>
          <SmartSearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="חיפוש מחלות..."
          />
        </SearchWrapper>
        <Button variant="primary" onClick={handleAddDisease}>
          <Plus size={16} />
          מחלה חדשה
        </Button>
      </TabHeader>

      <DataTable
        data={filteredDiseases}
        columns={columns}
        loading={loading}
      />

      <DiseaseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveDisease}
        disease={selectedDisease}
        title={selectedDisease ? 'עריכת מחלה' : 'הוספת מחלה חדשה'}
      />

      <ConfirmDialog
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title="מחיקת מחלה"
        message={`האם אתה בטוח שברצונך למחוק את המחלה "${diseaseToDelete?.name}"?`}
        confirmText="מחק"
        cancelText="ביטול"
      />
    </>
  );
};

export default DiseasesTab;
